// One hotel card can hold several room lines (e.g. 55 Double + 3 Single).
// Lines live in itinerary_items.room_allocations. To keep every older reader
// (calendar, supplier flows, costing) correct, the card also mirrors
// quantity = total rooms and cost = blended nightly rate, so
// cost x nights x quantity always equals the sum of the lines.

export type Occupancy = "single" | "double" | "triple";

export interface RoomAllocation {
  room_rate_id: string | null;
  room_type: string;
  occupancy: Occupancy;
  rooms: number;
  rate: number;
  currency?: string | null;
}

const PEOPLE: Record<Occupancy, number> = { single: 1, double: 2, triple: 3 };

export function parseAllocations(value: unknown): RoomAllocation[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((r) => r && typeof r === "object")
    .map((r: any) => ({
      room_rate_id: r.room_rate_id ?? null,
      room_type: String(r.room_type ?? ""),
      occupancy: (["single", "double", "triple"].includes(r.occupancy) ? r.occupancy : "double") as Occupancy,
      rooms: Math.max(0, Number(r.rooms) || 0),
      rate: Math.max(0, Number(r.rate) || 0),
      currency: r.currency ?? null,
    }));
}

export function totalRooms(lines: RoomAllocation[]): number {
  return lines.reduce((s, l) => s + l.rooms, 0);
}

export function nightlyTotal(lines: RoomAllocation[]): number {
  return lines.reduce((s, l) => s + l.rooms * l.rate, 0);
}

export function guestCapacity(lines: RoomAllocation[]): number {
  return lines.reduce((s, l) => s + l.rooms * PEOPLE[l.occupancy], 0);
}

/** Values to mirror onto the card's cost & quantity. */
export function mirrorFields(lines: RoomAllocation[]): { quantity: number; cost: number } {
  const rooms = totalRooms(lines);
  if (rooms <= 0) return { quantity: 1, cost: 0 };
  return { quantity: rooms, cost: nightlyTotal(lines) / rooms };
}

const OCC_LABEL: Record<Occupancy, string> = { single: "Single", double: "Double", triple: "Triple" };

/** "55 x Double, 3 x Single (Deluxe)" */
export function allocationSummary(value: unknown): string {
  const lines = parseAllocations(value).filter((l) => l.rooms > 0);
  if (lines.length === 0) return "";
  const parts = lines.map((l) => {
    const type = l.room_type ? ` ${l.room_type}` : "";
    return `${l.rooms} x ${OCC_LABEL[l.occupancy]}${type}`;
  });
  return `${parts.join(", ")} - ${guestCapacity(lines)} guests`;
}

/** Copy a room mix onto another hotel, re-pricing from its rate sheet when a matching room type/occupancy exists. */
export function copyMix(
  source: RoomAllocation[],
  rates: Array<{ id: string; room_type: string; single_rate?: number | null; double_rate?: number | null; triple_rate?: number | null; currency?: string | null }>,
): RoomAllocation[] {
  return source.map((l) => {
    const match = rates.find((r) => r.room_type?.toLowerCase() === l.room_type.toLowerCase()) ?? rates[0];
    const rate = match
      ? Number(l.occupancy === "single" ? match.single_rate : l.occupancy === "triple" ? match.triple_rate || match.double_rate : match.double_rate) || 0
      : 0;
    return {
      room_rate_id: match?.id ?? null,
      room_type: match?.room_type ?? l.room_type,
      occupancy: l.occupancy,
      rooms: l.rooms,
      rate,
      currency: match?.currency ?? l.currency ?? null,
    };
  });
}
